import React, { useState, useEffect, useRef } from "react";
import { Box, Container, Typography } from "@mui/material";
import axios from "axios";
import { useHistory } from "react-router-dom";
import web3 from "../../../Service/web3";
import "../../counter.css";
import "./hero.css";
import { colors } from "../../../../theme/colors";
import HeroStats from "./heroStates.js";
import HeroActions from "./heroActions";
import { AutoBetGlobe } from "./three";
import Ticker from "./ticker";
import heroBackground from "../../../../static/Images/heroBackground.png";
import {
  startMarqueeAnimation,
  startHeroBackgroundAnimation,
  startGlobeParallaxAnimation,
  startHeroEntryAnimation,
  cleanupAnimation,
  trustShineSx,
} from "../index.js";
import HeroTrustCards from "./heroTrustCards.js";

const Hero = ({
  item,
  bookies,
  lotteries,
  buytotal,
  draws,
  wins,
  variant,
}) => {
  const [featuredNews, setFeaturedNews] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [winnerNotifications, setWinnerNotifications] = useState([]);
  const [statsReady, setStatsReady] = useState(false);

  const autoBetRef = useRef(null);
  const heroBgRef = useRef(null);
  const heroContentRef = useRef(null);
  const globeRef = useRef(null);

  const apiUrl = process.env.REACT_APP_API_KEY;
  const history = useHistory();

  useEffect(() => {
    const animation = startMarqueeAnimation(autoBetRef);
    return () => cleanupAnimation(animation);
  }, []);

  useEffect(() => {
    const bgAnimation = startHeroBackgroundAnimation(heroBgRef);
    const globeAnimation = startGlobeParallaxAnimation(globeRef);

    return () => {
      if (bgAnimation) bgAnimation.revert();
      if (globeAnimation) globeAnimation.revert();
    };
  }, []);

  useEffect(() => {
    let animation;

    const startAnimation = () => {
      animation = startHeroEntryAnimation(heroContentRef, () => {
        setStatsReady(true);
      });
    };

    requestAnimationFrame(() => {
      requestAnimationFrame(startAnimation);
    });

    return () => cleanupAnimation(animation);
  }, []);

  useEffect(() => {
    axios.get(`${apiUrl}/news/getAllNews`).then((response) => {
      const newsData = response.data.result;
      const featuredNewsData = newsData.filter((news) => news.featured);
      setFeaturedNews(featuredNewsData);
    });

    const fetchData = async () => {
      try {
        const account = await web3.eth.getAccounts();
        let skip = 0;
        let allNotifications = [];
        let allWinnerNotifications = [];

        const fetchNotifications = async () => {
          const response = await fetch(
            `${apiUrl}/notifications?limit=10&skip=${skip}`,
            {
              method: "GET",
              headers: { Authorization: account[0] },
            }
          );

          if (response.ok) {
            const data = await response.json();

            const filteredNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryCreatedAdmin" ||
                notification.type === "LotteryCreated"
            );

            if (filteredNotifications.length > 0) {
              allNotifications = [
                ...allNotifications,
                ...filteredNotifications,
              ];

              skip += 10;
              await fetchNotifications();
            } else {
              setNotifications(allNotifications);
            }

            const filteredWinnerNotifications = data.result.filter(
              (notification) =>
                notification.type === "LotteryResult" ||
                notification.type === "SpinLotteryResult" ||
                notification.type === "LotteryWinner"
            );

            if (filteredWinnerNotifications.length > 0) {
              allWinnerNotifications = [
                ...allWinnerNotifications,
                ...filteredWinnerNotifications,
              ];

              skip += 10;
              await fetchNotifications();
            } else {
              setWinnerNotifications(allWinnerNotifications);
            }
          } else {
            console.error(
              "Error fetching notifications:",
              response.error
            );
          }
        };

        await fetchNotifications();
      } catch (error) {
        console.error("Error fetching notifications:", error);
      }
    };

    fetchData();
  }, []);

  function handleRedirect(notification) {
    switch (notification.type) {
      case "LotteryCreated":
        if (notification.lotteryName === "Missile")
          history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine")
          history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL")
          history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;

      case "LotteryCreatedAdmin":
        if (notification.lotteryName === "Missile")
          history.push(`/missilelottery/${notification.genId}`);
        else if (notification.lotteryName === "Mine")
          history.push(`/minelottery/${notification.genId}`);
        else if (notification.lotteryName === "MRL")
          history.push(`/lottery/${notification.genId}`);
        else history.push("/spinnerlottery");
        break;

      default:
        break;
    }
  }

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        p: 0,
        pt: "80px",
      }}
    >
      <Ticker
        featuredNews={featuredNews}
        notifications={notifications}
        winnerNotifications={winnerNotifications}
        handleRedirect={handleRedirect}
      />

      <Box
        className="banner-section"
        sx={(theme) => ({
          position: "relative",
          minHeight: "auto",
          height: "auto",
          overflow: "hidden",
          background: theme.palette.background.default,
        })}
      >
        <Box
          ref={heroBgRef}
          className="hero-bg-layer"
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            backgroundImage: `url(${heroBackground})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            willChange: "transform",
          }}
        />

        <Box
          ref={globeRef}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
            willChange: "transform",
            transform: {
              xs: "translate(15%, 10%)",
              sm: "none",
            },
          }}
        >
          <AutoBetGlobe />
        </Box>

        <Box
          className="banner-content-area"
          sx={(theme) => ({
            position: "relative",
            zIndex: 2,
            height: {
              xs: "auto",
              sm: "auto",
              md: "auto",
            },
            display: "flex",
            alignItems: "stretch",
            flexDirection: "column",
            pt: {
              xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
              sm: theme.custom.layoutSizes.section.paddingTop,
            },
            pb: {
              xs: theme.custom.layoutSizes.mobileStandard.padding.topBottom,
              sm: theme.custom.layoutSizes.section.paddingBottom,
            },
            px: {
              ...theme.custom.layoutSizes.homeSectionLeftPadding,
              xs: theme.custom.layoutSizes.mobileStandard.padding.leftRight,
            },
          })}
        >
          <Container
            maxWidth={false}
            disableGutters
            sx={(theme) => ({
              width: "100%",
              maxWidth: "1400px",
              mx: "auto",
              pt: 0,
              pb: 0,
              flex: {
                xs: "none",
                sm: "none",
                md: "none",
              },
              display: "flex",
              flexDirection: "column",
            })}
          >
            <Box
              ref={heroContentRef}
              sx={{
                width: "100%",
                maxWidth: {
                  xs: "100%",
                  sm: "900px",
                },
                flex: {
                  xs: "none",
                  sm: "none",
                  md: "none",
                },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                className={`banner-content${
                  variant === "home" ? " home-hero" : ""
                }`}
                sx={(theme) => ({
                  width: "100%",
                  justifyContent: "flex-start",
                  flex: {
                    xs: "none",
                    sm: "none",
                    md: "none",
                  },
                  display: "flex",
                  flexDirection: "column",
                  gap: {
                    xs: theme.custom.layoutSizes.section.gap.xs,
                    sm: theme.custom.layoutSizes.section.gap,
                  },
                })}
              >
                <Box
                  sx={(theme) =>
                    theme.typography.homeSection?.headerContainer || {}
                  }
                >
                  {variant === "home" && (
                    <Box
                      component="p"
                      className="hero-entry-item"
                      sx={(theme) => ({
                        ...(theme.typography.homeSection?.eyebrow || {}),
                        fontSize: {
                          xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.fontSize,
                          sm: undefined,
                        },
                        marginBottom: {
                          xs: theme.custom.layoutSizes.mobileStandard.typography.eyebrow.marginBottom,
                          sm: undefined,
                        },
                        color: theme.palette.primary.main,
                        "&::before": {
                          ...(theme.typography.homeSection?.eyebrow?.["&::before"] || {}),
                          background: theme.palette.primary.main,
                        },
                      })}
                    >
                      DECENTRALIZED LOTTERY & GAMING ECOSYSTEM
                    </Box>
                  )}

                  <Typography
                    component="h1"
                    className="title hero-entry-item"
                    sx={(theme) => ({
                      ...(theme.typography.homeSection?.heading || {}),
                      fontSize: {
                        xs: theme.custom.layoutSizes.mobileStandard.typography.heading.fontSize,
                        sm: theme.typography.homeSection?.heading?.fontSize,
                      },
                      margin: {
                        xs: theme.custom.layoutSizes.mobileStandard.typography.heading.margin,
                        sm: theme.typography.homeSection?.heading?.margin,
                      },
                      textAlign: "left",
                      color: theme.palette.text.primary,
                      whiteSpace: {
                        xs: "normal",
                        sm: "nowrap",
                      },
                    })}
                  >
                    Play. Win.{" "}
                    <Box
                      component="br"
                      sx={{ display: { xs: "block", sm: "none" } }}
                    />
                    <Box
                      component="span"
                      sx={(theme) => ({
                        ...trustShineSx(theme.palette.primary.main),
                        fontSize: {
                          xs: "1.2em",
                          sm: "1.2em",
                        },
                        fontWeight: 900,
                        WebkitTextStroke: `1.5px ${theme.palette.primary.main}`,
                      })}
                    >
                      Trust.
                    </Box>
                  </Typography>

                  <Typography
                    component="p"
                    className="hero-entry-item hero-description"
                    sx={(theme) => ({
                      ...(theme.typography.homeSection?.description || {}),
                      fontSize: {
                        xs: theme.custom.layoutSizes.mobileStandard.typography.paragraph.fontSize,
                        sm: theme.typography.homeSection?.description?.fontSize,
                      },
                      marginTop: {
                        xs: theme.custom.layoutSizes.mobileStandard.typography.paragraph.marginTop,
                        sm: theme.typography.homeSection?.description?.marginTop,
                      },
                      color: {
                        xs: theme.palette.text.primary,
                        sm: theme.palette.text.secondary,
                      },
                      textShadow: {
                        xs: `0 0 4px ${
                          theme.palette.background.default || "#ffffff"
                        }, 0 0 1px ${
                          theme.palette.background.default || "#ffffff"
                        }`,
                        sm: "none",
                      },
                      maxWidth: "800px",
                    })}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: "none",
                          sm: "inline",
                        },
                        fontSize: "inherit",
                      }}
                    >
                      AutoBET is a next-generation blockchain lottery and
                      gaming platform featuring fair, transparent and globally
                      accessible games like Powerball, Revolver, Pokies and more
                      — all powered by smart contracts.
                    </Box>
                    <Box
                      component="span"
                      sx={{
                        display: {
                          xs: "inline",
                          sm: "none",
                        },
                        fontSize: "inherit",
                      }}
                    >
                      AutoBET is a fair, transparent and globally accessible
                      lottery and gaming platform with games like Powerball,
                      Revolver, Pokies and more — powered by smart contracts.
                    </Box>
                  </Typography>
                </Box>

                {variant === "home" && (
                  <Box
                    className="hero-entry-item"
                    sx={{ order: { xs: 1, sm: 3 }, mt: 0 }}
                  >
                    <HeroTrustCards />
                  </Box>
                )}

                <Box
                  sx={{
                    order: { xs: 2, sm: 1 },
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    background: { xs: "rgba(244, 248, 255, 0.95)", sm: "transparent" },
                    borderRadius: { xs: "24px", sm: 0 },
                    p: { xs: 2.5, sm: 0 },
                    mt: { xs: 2, sm: 0 },
                  }}
                >
                  <Box className="hero-entry-item">
                    <HeroStats
                      bookies={bookies}
                      lotteries={lotteries}
                      buytotal={buytotal}
                      draws={draws}
                      wins={wins}
                      startAnimation={statsReady}
                    />
                  </Box>
                </Box>

                {variant === "home" && (
                  <Box
                    className="hero-entry-item"
                    sx={{
                      order: { xs: 3, sm: 2 },
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      mt: { xs: 2, sm: 0 },
                    }}
                  >
                    <HeroActions history={history} />
                  </Box>
                )}
              </Box>
            </Box>
          </Container>
        </Box>
      </Box>
    </Container>
  );
};

export default Hero;